import React from "react"

import { ArrowDown, ArrowUp, Minus } from "lucide-react"

import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

interface MetricCardProps {
  title: string
  value: string | number
  percentChange: number
  icon: React.ReactNode
  isLoading?: boolean
  error?: boolean
  reverse?: boolean // When true, negative change is considered positive (e.g., for costs)
  description?: string // Optional description text
  previousValue?: string // Optional previous period value
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  percentChange,
  icon,
  isLoading = false,
  error = false,
  reverse = false, // Default: positive change is good
  description,
  previousValue: _previousValue, // Renamed to avoid unused variable warning
}) => {
  // Format the percentage change
  const formattedPercentChange = Math.abs(percentChange).toFixed(1)
  const isPositive = percentChange > 0
  const isNeutral = percentChange === 0

  // Determine if the outcome is positive based on the reverse prop
  // When reverse=true (e.g. for costs): negative change is good
  // When reverse=false (default): positive change is good
  const isPositiveOutcome = reverse ? !isPositive : isPositive

  return (
    <Card>
      <CardContent className="p-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        ) : error ? (
          <div className="text-red-500">Error loading data</div>
        ) : (
          <div className="flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                {icon}
              </div>
            </div>
            <div className="text-2xl font-bold">{value}</div>
            {description && <div className="text-xs text-muted-foreground mt-1">{description}</div>}
            <div className="flex items-center text-sm pt-2">
              {isNeutral ? (
                <>
                  <div className={`mr-1 rounded-full p-1 bg-palette-jungleGreen100`}>
                    <Minus className="h-3 w-3 text-palette-jungleGreen600" />
                  </div>
                  <span className="text-palette-jungleGreen600">No change</span>
                  <span className="ml-1 text-muted-foreground">vs previous period</span>
                </>
              ) : (
                <>
                  <div
                    className={`mr-1 rounded-full p-1 ${
                      isPositiveOutcome ? "bg-green-100" : "bg-red-100"
                    }`}
                  >
                    {isPositive ? (
                      <ArrowUp
                        className={`h-3 w-3 ${
                          isPositiveOutcome ? "text-green-600" : "text-red-600"
                        }`}
                      />
                    ) : (
                      <ArrowDown
                        className={`h-3 w-3 ${
                          isPositiveOutcome ? "text-green-600" : "text-red-600"
                        }`}
                      />
                    )}
                  </div>
                  <span className={isPositiveOutcome ? "text-green-600" : "text-red-600"}>
                    {formattedPercentChange}%
                  </span>
                  <span className="ml-1 text-muted-foreground">vs previous period</span>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
